// Implements: F-UIX-018, F-USG-005
/**
 * The daily spend as an inline SVG bar chart (the redesign mockup's "Gasto diario"), with the same
 * figures as a table one toggle away. Pure presentation; money is formatted here, in the backoffice
 * only (OD-12), and each day's bar carries it in its own name, so the chart is readable without
 * seeing it. Only the days the report lists are drawn, one bar each; a day with no spend is a flat
 * bar. One currency at a time: the caller picks it.
 *
 * Bars are `role="img"` with the day and the amount as their name (and an SVG `<title>` for the
 * pointer); they are not tab stops, because the table is the keyboard and screen reader way in.
 * Colours come from semantic aliases in the CSS module, never from here.
 */

import { useEffect, useId, useRef, useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { moneyText } from "../../../shared/ui/moneyText";
import { PLATFORM_LOCALE } from "../../../types/locale";
import { calendarDayText } from "../model/myUsage";
import { niceScale } from "../model/chartScale";
import styles from "./DailySpendChart.module.css";

export interface DailySpendPoint {
  /** `YYYY-MM-DD`, the report's calendar day. */
  readonly day: string;
  readonly costMicros: number;
}

export interface DailySpendChartProps {
  readonly days: readonly DailySpendPoint[];
  readonly currency: string;
}

const MICROS = 1_000_000;
const DEFAULT_WIDTH = 640;
const MIN_WIDTH = 320;
const HEIGHT = 220;
const LEFT = 40;
const TOP = 8;
const BOTTOM = 22;
const MAX_BAR = 22;
const LABEL_EVERY = 5;

const AXIS = new Intl.NumberFormat(PLATFORM_LOCALE, { maximumFractionDigits: 6 });

/** The day of the month of a `YYYY-MM-DD`, for the axis. */
function dayOfMonth(day: string): string {
  return String(Number(day.slice(8, 10)));
}

export function DailySpendChart({ days, currency }: DailySpendChartProps): ReactElement {
  const { t } = useTranslation();
  const titleId = useId();
  const [asTable, setAsTable] = useState(false);
  // Drawn at the width of its box (as the mockup does), so the axis text keeps its own size.
  const box = useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = useState(DEFAULT_WIDTH);
  useEffect(() => {
    const element = box.current;
    if (element === null || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width !== undefined && width > 0) setMeasured(Math.max(MIN_WIDTH, Math.round(width)));
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [asTable]);
  const WIDTH = measured;
  const label = t("backoffice.consumption.chart.label", { currency });
  const highest = Math.max(0, ...days.map((point) => point.costMicros)) / MICROS;
  const scale = niceScale(highest);
  const plotHeight = HEIGHT - TOP - BOTTOM;
  const step = (WIDTH - LEFT) / Math.max(1, days.length);
  const barWidth = Math.max(2, Math.min(MAX_BAR, step - 6));
  const yOf = (value: number): number => TOP + plotHeight * (1 - value / scale.max);

  return (
    <section className={styles.card} aria-labelledby={titleId}>
      <div className={styles.head}>
        <h3 id={titleId} className={styles.title}>
          {t("backoffice.consumption.chart.title")}
        </h3>
        <span className={styles.unit}>{currency}</span>
        <span className={styles.spacer} />
        <Button variant="ghost" pressed={asTable} onClick={() => { setAsTable((value) => !value); }}>
          {t("backoffice.consumption.chart.asTable")}
        </Button>
      </div>
      {asTable ? (
        <div className={styles.tableWrap}>
          <table className={styles.table} aria-label={t("backoffice.consumption.chart.tableLabel", { currency })}>
            <thead>
              <tr>
                <th scope="col">{t("backoffice.consumption.chart.day")}</th>
                <th scope="col" className={styles.number}>
                  {t("backoffice.consumption.chart.spend")}
                </th>
              </tr>
            </thead>
            <tbody>
              {days.map((point) => (
                <tr key={point.day}>
                  <th scope="row">{calendarDayText(point.day)}</th>
                  <td className={styles.number}>{moneyText(point.costMicros, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div ref={box} className={styles.chart}>
          <svg viewBox={`0 0 ${String(WIDTH)} ${String(HEIGHT)}`} role="group" aria-label={label} className={styles.svg}>
            {scale.ticks.map((tick) => (
              <g key={tick}>
                <line className={styles.grid} x1={LEFT} x2={WIDTH} y1={yOf(tick)} y2={yOf(tick)} />
                <text className={styles.axis} x={LEFT - 8} y={yOf(tick) + 4} textAnchor="end">
                  {AXIS.format(tick)}
                </text>
              </g>
            ))}
            {days.map((point, index) => {
              const height = plotHeight * ((point.costMicros / MICROS) / scale.max);
              const x = LEFT + index * step + (step - barWidth) / 2;
              const name = `${calendarDayText(point.day)}: ${moneyText(point.costMicros, currency)}`;
              return (
                <g key={point.day}>
                  <rect
                    className={styles.bar}
                    role="img"
                    aria-label={name}
                    data-height={Math.round(height)}
                    x={x}
                    y={TOP + plotHeight - height}
                    width={barWidth}
                    height={height}
                    rx={Math.min(4, height / 2)}
                  >
                    <title>{name}</title>
                  </rect>
                  {index % LABEL_EVERY === 0 || index === days.length - 1 ? (
                    <text className={styles.axis} x={x + barWidth / 2} y={HEIGHT - 6} textAnchor="middle">
                      {dayOfMonth(point.day)}
                    </text>
                  ) : null}
                </g>
              );
            })}
          </svg>
        </div>
      )}
    </section>
  );
}
